/* eslint-disable max-classes-per-file */
import { type QueryFilters as TanstackQueryFilters } from '@tanstack/react-query';

import { type AssetsViewParams } from '../types';

const serializeForQueryKey = ( { projectID, folderID, filters, sorting }: Omit<AssetsViewParams, 'flatView'> ) => ( {
	projectID, folderID, ...filters?.toJson(), ...sorting?.toJson()
} );

class QueryKeys {
	static forAssetList( params: AssetsViewParams ) {
		return [ 'assets', 'list', { ...serializeForQueryKey( params ), flatView: params.flatView } ];
	}

	static forFolderList( params: Omit<AssetsViewParams, 'flatView'> ) {
		return [ 'folders', 'list', serializeForQueryKey( params ) ];
	}

	static forItemsCount( params: AssetsViewParams ) {
		return [ 'itemsCount', { ...serializeForQueryKey( params ), flatView: params.flatView } ];
	}

	static forFolderTree( projectID: number ) {
		return [ 'folderTree', projectID ];
	}

	static forMaxRoundNumber( projectID: number ) {
		return [ 'maxRoundNumber', projectID ];
	}

	static forOrganizerOverview( { projectID, folderID }: { projectID: number, folderID: number | null } ) {
		return [ 'overview', projectID, folderID ];
	}
}

class QueryFilters {
	static forAssetAndFolderLists( {
		projectID, folderID, only, queryType = 'all'
	}: {
		projectID: number,
		folderID?: number | null,
		only?: 'assets' | 'folders',
		queryType?: TanstackQueryFilters[ 'type' ]
	} ): TanstackQueryFilters {
		return {
			type: queryType,
			predicate: ( { queryKey: key } ) => (
				( only
					? key[ 0 ] === only
					: ( key[ 0 ] === 'assets' || key[ 0 ] === 'folders' )
				)
				&& key[ 1 ] === 'list'
				&& key[ 2 ] instanceof Object
				&& 'projectID' in key[ 2 ]
				&& 'folderID' in key[ 2 ]
				&& key[ 2 ].projectID === projectID
				&& ( folderID === undefined || key[ 2 ].folderID === folderID )
			)
		};
	}

	static forItemsCount( {
		projectID, folderID
	}: {
		projectID: number, folderID?: number | null
	} ): TanstackQueryFilters {
		return {
			predicate: ( { queryKey: key } ) => (
				key[ 0 ] === 'itemsCount'
				&& key[ 1 ] instanceof Object
				&& 'projectID' in key[ 1 ]
				&& 'folderID' in key[ 1 ]
				&& key[ 1 ].projectID === projectID
				&& ( folderID === undefined || key[ 1 ].folderID === folderID )
			)
		};
	}

	static forProjectOverviews( { projectID }: { projectID: number } ): TanstackQueryFilters {
		return {
			type: 'all',
			predicate: ( { queryKey: key } ) => (
				key[ 0 ] === 'overview'
				&& key[ 1 ] === projectID
			)
		};
	}

	static forAllOverviews(): TanstackQueryFilters {
		return {
			type: 'all',
			predicate: ( { queryKey: key } ) => key[ 0 ] === 'overview'
		};
	}
}

export { QueryKeys, QueryFilters };
